import { bbox } from "@turf/turf";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { LngLatBoundsLike, Map } from "react-map-gl/maplibre";
import { mapTransformRequest } from "../../../../../API/AureaApi";
import { mapStyle } from "../../../../../Component/Map";
import BaseLayer from "../../../../../Component/Map/Layers/BaseLayer";
import ResultSetLayer from "../../../../../Component/Map/Layers/ResultSetLayer";
import TaskmapWizard from "./TaskmapWizard";
import { useTranslation } from "react-i18next";
import { TaskmapProduct } from "../../../../../API/taskmap";
import { DownloadTaskmapStep } from "./DownloadTaskmapStep";
import { TaskmapProps } from "../Taskmap";
import { getFileNameFromHeader } from "../utils/file-utils";
import { getTaskmap } from "../../../../../API/taskmap";
import TaskmapPopup from "../TaskmapPopup";
import TaskmapConfigHistoryModal from "../TaskmapConfigHistoryModal";
import TaskmapStepHeader from "./TaskmapStepHeader";
import { useParams } from "react-router";
import { getSortedData, getCutoffs } from "../utils/data-utils";
import {
  belongsToFamily,
  downloadTaskmap,
  getTaskmapHistory,
  TaskmapHistoryEntry,
} from "../../../../../API/taskmapConfigHistory";
import { CutoffRange } from "../taskmap.types";

const rate = { low: 0, high: 100 };

export default function BlossomTaskmap({
  geojson,
  productKey,
}: TaskmapProps): React.ReactNode {
  const { t } = useTranslation();

  const [cutoff, setCutoff] = useState<CutoffRange>({ min: 1, max: 1 });
  const [machine, setMachine] = useState<string>("");
  const [terminal, setTerminal] = useState<string>("");
  const [error, setError] = useState<string | null>(null);

  const productHeaders: { [key: string]: string } = {
    blossom_area_cm2: t("heading.Blossom area"),
    blossom_cluster_count: t("heading.Blossom count"),
  };

  const totalTreeCount = geojson.features.length;

  const { resultSetId, fieldId } = useParams();
  const [popupOpen, setPopupOpen] = useState<boolean>(false);
  const [taskmap, setTaskmap] = useState<File | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState<TaskmapHistoryEntry[]>([]);

  useEffect(() => {
    if (!fieldId) return;
    getTaskmapHistory(fieldId)
      .then((entries) =>
        setHistory(
          entries.filter((entry) =>
            belongsToFamily(entry.product_sku, "blossom"),
          ),
        ),
      )
      .catch(() => setHistory([]));
  }, [fieldId]);

  const useSprayParameter =
    terminal === "mueller" && machine === "munckhof_trailed_sprayer";

  const lowRate = useSprayParameter ? 0 : rate.low;
  const highRate = useSprayParameter ? 1 : rate.high;

  const onSubmit = useCallback(async () => {
    setLoading(true);
    setError(null);
    const response = await getTaskmap(
      {
        terminal: {
          type: terminal,
        },
        parameters: {
          cutoff_low: `${cutoff.min}`,
          cutoff_high: `${cutoff.max}`,
          rate_low: lowRate,
          rate_high: highRate,
          machine_parameters: {
            type: machine,
          },
        },
      },
      resultSetId,
    );

    setLoading(false);

    if (!response || !response.ok) {
      setError(t("text.error_occured"));
      return;
    }
    setPopupOpen(true);
    setTaskmap(
      new File([await response.blob()], getFileNameFromHeader(response)),
    );
    if (fieldId) {
      getTaskmapHistory(fieldId)
        .then((entries) =>
          setHistory(
            entries.filter((entry) =>
              belongsToFamily(entry.product_sku, "blossom"),
            ),
          ),
        )
        .catch(() => {});
    }
  }, [machine, terminal, cutoff]);

  const onDownloadAgain = useCallback(async (entry: TaskmapHistoryEntry) => {
    setHistoryOpen(false);
    setLoading(true);
    setError(null);
    const response = await downloadTaskmap(entry.id);

    setLoading(false);

    if (!response || !response.ok) {
      setError(t("text.error_occured"));
      return;
    }
    setPopupOpen(true);
    setTaskmap(
      new File([await response.blob()], getFileNameFromHeader(response)),
    );
  }, []);

  const sortedData = useMemo(
    () => getSortedData(geojson, productKey),
    [geojson, productKey],
  );

  useEffect(() => setCutoff(getCutoffs(sortedData)), [sortedData]);

  return (
    <>
      <TaskmapWizard
        product={TaskmapProduct.BLOSSOM}
        setMachine={setMachine}
        setTerminal={setTerminal}
      >
        <DownloadTaskmapStep
          cutoff={cutoff}
          setCutoff={setCutoff}
          header={
            <TaskmapStepHeader
              label={productHeaders[productKey]}
              showHistory={history.length > 0}
              onHistoryClick={() => setHistoryOpen(true)}
            />
          }
          label={productHeaders[productKey]}
          sortedData={sortedData}
          totalTreeCount={totalTreeCount}
          onSubmit={onSubmit}
          loading={loading}
        />
      </TaskmapWizard>
      {error && (
        <div style={{ color: "red", padding: "8px 16px" }}>{error}</div>
      )}
      <Map
        mapStyle={mapStyle}
        transformRequest={mapTransformRequest}
        initialViewState={{
          bounds: bbox(geojson) as LngLatBoundsLike,
        }}
        style={{ width: "100%", height: "70vh" }}
      >
        <BaseLayer />
        <ResultSetLayer geojson={geojson} cutoff={cutoff} />
      </Map>
      <TaskmapPopup
        taskmap={taskmap}
        open={popupOpen}
        setOpen={(open) => {
          setPopupOpen(open);
          if (!open) {
            setTaskmap(null);
          }
        }}
      ></TaskmapPopup>
      <TaskmapConfigHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        product="blossom"
        history={history}
        onDownloadAgain={onDownloadAgain}
      />
    </>
  );
}
